import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
  type MouseEvent,
  type KeyboardEvent,
  type CSSProperties,
} from 'react';

// Context for dropdown state
interface DropdownContextValue {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  triggerRef: React.RefObject<HTMLElement>;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

function useDropdownContext() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('Dropdown components must be used within a Dropdown');
  }
  return context;
}

// ============================================================================
// Dropdown Container
// ============================================================================

export interface DropdownProps {
  children: ReactNode;
  /** Additional class name */
  className?: string;
  /** Inline styles */
  style?: CSSProperties;
}

/**
 * Dropdown - Container component for dropdown menus
 *
 * @example
 * ```tsx
 * <Dropdown>
 *   <DropdownTrigger>
 *     <button>Open Menu</button>
 *   </DropdownTrigger>
 *   <DropdownMenu>
 *     <DropdownItem onClick={() => {}}>Action 1</DropdownItem>
 *     <DropdownItem onClick={() => {}}>Action 2</DropdownItem>
 *   </DropdownMenu>
 * </Dropdown>
 * ```
 */
export function Dropdown({ children, className = '', style }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close();
      }
    };

    // Use setTimeout to avoid closing immediately when opening
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, close]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  const contextValue: DropdownContextValue = {
    isOpen,
    toggle,
    close,
    triggerRef,
  };

  return (
    <DropdownContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={`dropdown ${isOpen ? 'show' : ''} ${className}`}
        style={{ position: 'relative', ...style }}
      >
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

// ============================================================================
// Dropdown Trigger
// ============================================================================

export interface DropdownTriggerProps {
  children: ReactNode;
  /** Additional class name */
  className?: string;
  /** Render as a different element (default: button) */
  as?: 'button' | 'a' | 'div';
  /** Test ID for testing */
  'data-test-id'?: string;
}

/**
 * DropdownTrigger - The button/element that opens the dropdown
 */
export function DropdownTrigger({
  children,
  className = '',
  as: Component = 'button',
  'data-test-id': testId,
}: DropdownTriggerProps) {
  const { isOpen, toggle, triggerRef } = useDropdownContext();

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      toggle();
    },
    [toggle]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'ArrowDown' && !isOpen) {
        event.preventDefault();
        toggle();
      }
    },
    [toggle, isOpen]
  );

  const props = {
    ref: triggerRef as any,
    className: `${className}`,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    'aria-haspopup': 'true' as const,
    'aria-expanded': isOpen,
    'data-test-id': testId,
    ...(Component === 'button' ? { type: 'button' as const } : {}),
    ...(Component === 'a' ? { href: '#' } : {}),
  };

  return <Component {...props}>{children}</Component>;
}

// ============================================================================
// Dropdown Menu
// ============================================================================

export interface DropdownMenuProps {
  children: ReactNode;
  /** Alignment of the menu */
  align?: 'start' | 'end';
  /** Additional class name */
  className?: string;
  /** Inline styles */
  style?: CSSProperties;
}

/**
 * DropdownMenu - The menu container that appears when dropdown is open
 */
export function DropdownMenu({ children, align = 'start', className = '', style }: DropdownMenuProps) {
  const { isOpen } = useDropdownContext();
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus first item when menu opens
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const firstItem = menuRef.current.querySelector('[role="menuitem"]:not([disabled])') as HTMLElement;
      firstItem?.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const alignmentStyle: CSSProperties =
    align === 'end' ? { right: 0, left: 'auto' } : { left: 0, right: 'auto' };

  return (
    <div
      ref={menuRef}
      className={`dropdown-menu show ${className}`}
      role="menu"
      style={{
        position: 'absolute',
        top: '100%',
        zIndex: 1000,
        minWidth: '10rem',
        padding: '0.5rem 0',
        margin: '0.125rem 0 0',
        backgroundColor: '#fff',
        border: '1px solid rgba(0, 0, 0, 0.15)',
        borderRadius: '0.375rem',
        boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.175)',
        ...alignmentStyle,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ============================================================================
// Dropdown Item
// ============================================================================

export interface DropdownItemProps {
  children: ReactNode;
  /** Click handler */
  onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  /** Link href (renders as <a> if provided) */
  href?: string;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
  /** Test ID for testing */
  'data-test-id'?: string;
  /** Close dropdown on click (default: true) */
  closeOnClick?: boolean;
  /** Turbo method for links */
  'data-turbo-method'?: string;
  /** Turbo confirm for links */
  'data-turbo-confirm'?: string;
}

/**
 * DropdownItem - A single item in the dropdown menu
 */
export function DropdownItem({
  children,
  onClick,
  href,
  disabled = false,
  className = '',
  'data-test-id': testId,
  closeOnClick = true,
  'data-turbo-method': turboMethod,
  'data-turbo-confirm': turboConfirm,
}: DropdownItemProps) {
  const { close } = useDropdownContext();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }

      onClick?.(event);

      if (closeOnClick && !event.defaultPrevented) {
        close();
      }
    },
    [disabled, onClick, closeOnClick, close]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick(event as unknown as MouseEvent<HTMLButtonElement | HTMLAnchorElement>);
      }
    },
    [handleClick]
  );

  const itemStyle: CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '0.25rem 1rem',
    clear: 'both',
    fontWeight: 400,
    color: disabled ? '#adb5bd' : '#212529',
    textAlign: 'left' as const,
    textDecoration: 'none',
    whiteSpace: 'nowrap' as const,
    backgroundColor: 'transparent',
    border: 0,
    cursor: disabled ? 'default' : 'pointer',
  };

  const commonProps = {
    className: `dropdown-item ${disabled ? 'disabled' : ''} ${className}`,
    role: 'menuitem',
    tabIndex: disabled ? -1 : 0,
    'aria-disabled': disabled,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    style: itemStyle,
    'data-test-id': testId,
  };

  if (href) {
    return (
      <a
        {...commonProps}
        href={disabled ? undefined : href}
        data-turbo-method={turboMethod}
        data-turbo-confirm={turboConfirm}
      >
        {children}
      </a>
    );
  }

  return (
    <button {...commonProps} type="button" disabled={disabled}>
      {children}
    </button>
  );
}

// ============================================================================
// Dropdown Link Item (for React Router)
// ============================================================================

export interface DropdownLinkItemProps {
  children: ReactNode;
  /** Link destination (for React Router Link) */
  to: string;
  /** Whether the item is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
  /** Close dropdown on click (default: true) */
  closeOnClick?: boolean;
}

/**
 * useDropdownClose - Hook to get the close function for custom dropdown items
 * Use this when you need to close the dropdown from a custom component like React Router Link
 */
export function useDropdownClose() {
  const context = useContext(DropdownContext);
  return context?.close ?? (() => {});
}

// ============================================================================
// Dropdown Divider
// ============================================================================

export interface DropdownDividerProps {
  className?: string;
}

/**
 * DropdownDivider - A horizontal divider line
 */
export function DropdownDivider({ className = '' }: DropdownDividerProps) {
  return (
    <hr
      className={`dropdown-divider ${className}`}
      style={{
        height: 0,
        margin: '0.5rem 0',
        overflow: 'hidden',
        borderTop: '1px solid rgba(0, 0, 0, 0.15)',
      }}
    />
  );
}

// ============================================================================
// Dropdown Header
// ============================================================================

export interface DropdownHeaderProps {
  children: ReactNode;
  className?: string;
}

/**
 * DropdownHeader - A non-interactive header text
 */
export function DropdownHeader({ children, className = '' }: DropdownHeaderProps) {
  return (
    <h6
      className={`dropdown-header ${className}`}
      style={{
        display: 'block',
        padding: '0.5rem 1rem',
        marginBottom: 0,
        fontSize: '0.875rem',
        color: '#6c757d',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </h6>
  );
}

export default Dropdown;
